import { initializeApp, type FirebaseApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check";
import { getAuth, connectAuthEmulator, type Auth } from "firebase/auth";
import { getFirestore, connectFirestoreEmulator, type Firestore } from "firebase/firestore";
import { getFunctions, connectFunctionsEmulator, type Functions } from "firebase/functions";
import { requireEnv, requireEnvBoolean } from "@/lib/requireEnv";

/**
 * Firebase singletons.
 *
 * The same shape as the back office's, and for the same reason: every value is
 * read through `requireEnv`, so a missing variable throws at startup with the
 * variable named rather than producing an application that loads and then fails
 * on the first call.
 */

let app: FirebaseApp | undefined;

function ensureApp(): FirebaseApp {
  if (app) return app;

  app = initializeApp({
    apiKey: requireEnv("VITE_FIREBASE_API_KEY"),
    authDomain: requireEnv("VITE_FIREBASE_AUTH_DOMAIN"),
    projectId: requireEnv("VITE_FIREBASE_PROJECT_ID"),
    appId: requireEnv("VITE_FIREBASE_APP_ID"),
  });

  /*
   * App Check, when this deployment is meant to have it.
   *
   * Explicitly switched rather than inferred from whether a key happens to be
   * set: a back office that quietly stopped attesting itself would look exactly
   * like one that never did, and "is App Check on in production?" must have an
   * answer you can read off the configuration. `VITE_APP_CHECK=false` is a
   * decision; a missing `VITE_APP_CHECK` is a mistake, and throws.
   *
   * Google's own alert is what prompted this: reCAPTCHA Enterprise keys were
   * created for this app and for the admin console and neither app had ever
   * called App Check, so the keys protected nothing (2026-09-14). It goes here,
   * immediately after `initializeApp` and before any service is fetched,
   * because a token must exist before the first call that carries one.
   */
  if (requireEnvBoolean("VITE_APP_CHECK")) {
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(requireEnv("VITE_RECAPTCHA_SITE_KEY")),
      isTokenAutoRefreshEnabled: true,
    });
  }

  if (requireEnvBoolean("VITE_USE_EMULATOR")) {
    const host = requireEnv("VITE_EMULATOR_HOST");
    connectAuthEmulator(getAuth(app), `http://${host}:9099`, { disableWarnings: true });
    connectFirestoreEmulator(getFirestore(app), host, 8080);
    connectFunctionsEmulator(getFunctions(app, requireEnv("VITE_FUNCTIONS_REGION")), host, 5001);

    // Loud on purpose. An admin console pointed at the wrong project is the one
    // place in this product where a mistake reaches every customer at once.
    // eslint-disable-next-line no-console
    console.info("[spontar admin] connected to the local emulator suite");
  }

  return app;
}

export const firebaseApp = (): FirebaseApp => ensureApp();
export const auth = (): Auth => getAuth(ensureApp());
export const db = (): Firestore => getFirestore(ensureApp());
export const functions = (): Functions =>
  getFunctions(ensureApp(), requireEnv("VITE_FUNCTIONS_REGION"));
